<template>
  <div class="chat-area" :class="{ 'chat-area--dark': isDarkMode }">
    <header class="chat-area__header" :class="{ 'chat-area__header--dark': isDarkMode }">
      <button
        class="chat-area__toggle-btn"
        :class="{ 'chat-area__toggle-btn--show': !sidebarOpen }"
        type="button"
        @click="$emit('toggle-sidebar')"
        :aria-label="sidebarOpen ? 'Close sidebar' : 'Open sidebar'"
      >
        <font-awesome-icon :icon="sidebarToggleIcon" />
      </button>
      <div class="chat-area__header-brand">
        <span class="chat-area__avatar" aria-hidden="true">
          <i class="fas fa-robot"></i>
        </span>
        <div class="chat-area__header-text">
          <span class="chat-area__title">Disha AI</span>
          <span class="chat-area__subtitle-header">{{ headerSubtitle }}</span>
        </div>
      </div>
    </header>

    <main class="chat-area__body">
      <div v-if="loading" class="chat-area__center">
        <div class="spinner spinner-large spinner-slow"></div>
        <p class="chat-area__loading-text">Loading conversation…</p>
      </div>

      <div v-else-if="showWelcome" class="chat-area__scroll chat-area__center">
        <div class="chat-area__welcome">
          <div class="chat-area__welcome-icon" aria-hidden="true">
            <i class="fas fa-magic"></i>
          </div>
          <h2 class="chat-area__greeting">Hello 👋</h2>
          <p class="chat-area__subtitle">What would you like to explore today?</p>
          <span class="chat-area__helper">I can help you with:</span>
          <div class="chat-area__topics">
            <span class="chat-area__topic-pill" v-for="topic in topics" :key="topic">{{ topic }}</span>
          </div>
          <div class="chat-area__suggestions">
            <button
              type="button"
              class="chat-area__suggestion-chip"
              v-for="suggestion in suggestions"
              :key="suggestion"
              @click="handleSend(suggestion)"
            >
              {{ suggestion }}
            </button>
          </div>
        </div>
      </div>

      <div v-else class="chat-area__messages-panel">
        <MessageDisplay @followup="handleSend" />
        <div v-if="inlineError" class="chat-area__error" :class="{ 'chat-area__error--dark': isDarkMode }">
          <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
          <span>{{ inlineError }}</span>
          <button v-if="showRetry" type="button" class="chat-area__retry-btn" @click="retrySend"> Retry </button>
        </div>
      </div>
    </main>

    <MessageInput v-if="!loading" @send="handleSend" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import MessageDisplay from './MessageDisplay.vue';
import MessageInput from './MessageInput.vue';

export default {
  name: 'ChatArea',
  components: { MessageDisplay, MessageInput },
  props: {
    sidebarOpen: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      lastMessage: null,
      topics: ['📋 Audits', '🏪 Stores', '✅ Tasks', '📊 Reports', '⚙️ Settings'],
      suggestions: [
        'Show my pending audits',
        'List stores near me',
        'What tasks are due today?',
        'Show recent reports',
        'Compare audit scores'
      ]
    };
  },
  computed: {
    ...mapState('chat', ['loading', 'streaming', 'error', 'messages', 'activeConversation']),
    ...mapState('darkMode', ['isDarkMode']),
    sidebarToggleIcon() {
      return this.sidebarOpen ? faChevronLeft : faChevronRight;
    },
    showWelcome() {
      return !this.activeConversation && this.messages.length === 0;
    },
    headerSubtitle() {
      if (this.streaming) return 'Typing…';
      if (this.activeConversation && this.activeConversation.title) {
        const title = this.activeConversation.title;
        return title.length > 42 ? `${title.substring(0, 42)}…` : title;
      }
      return 'Your retail operations assistant';
    },
    inlineError() {
      if (!this.error) return null;
      if (this.error.includes('503') || this.error.includes('unavailable')) {
        return 'AI service temporarily unavailable';
      }
      return this.error;
    },
    showRetry() {
      if (!this.error) return false;
      return !this.error.includes('503') && !this.error.includes('unavailable');
    }
  },
  methods: {
    handleSend(text) {
      this.lastMessage = text;
      this.$store.dispatch('chat/sendMessage', { message: text });
    },
    retrySend() {
      if (this.lastMessage) {
        this.$store.dispatch('chat/sendMessage', { message: this.lastMessage });
      }
    }
  }
};
</script>

<style scoped>
.chat-area {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  color: #2c3e50;
}

.chat-area--dark {
  background: #2c3034;
  color: #f0f0f0;
}

.chat-area__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  z-index: 2;
}

.chat-area__header--dark {
  background: #343a40;
  border-bottom-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.chat-area__header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.chat-area__avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f28a3e 0%, #e86a1a 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(242, 138, 62, 0.35);
}

.chat-area__header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chat-area__title {
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.3;
}

.chat-area__subtitle-header {
  font-size: 0.8rem;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-area--dark .chat-area__subtitle-header {
  color: #aaa;
}

.chat-area__toggle-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(242, 138, 62, 0.1);
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  cursor: pointer;
  color: #f28a3e;
  flex-shrink: 0;
  transition: background 0.15s;
}

.chat-area__toggle-btn--show {
  display: inline-flex;
}

.chat-area__toggle-btn:hover {
  background: rgba(242, 138, 62, 0.2);
}

.chat-area--dark .chat-area__toggle-btn {
  background: rgba(242, 138, 62, 0.15);
  color: #f5a55a;
}

.chat-area__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-area__scroll {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.chat-area__center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  min-height: 0;
}

.chat-area__loading-text {
  margin-top: 12px;
  font-size: 0.9rem;
  color: #888;
}

.chat-area--dark .chat-area__loading-text {
  color: #aaa;
}

.chat-area__messages-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-area__welcome {
  text-align: center;
  max-width: 520px;
  padding: 16px 24px 32px;
  width: 100%;
}

.chat-area__welcome-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(242, 138, 62, 0.15) 0%, rgba(242, 138, 62, 0.05) 100%);
  color: #f28a3e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.chat-area--dark .chat-area__welcome-icon {
  background: linear-gradient(135deg, rgba(242, 138, 62, 0.25) 0%, rgba(242, 138, 62, 0.08) 100%);
  color: #f5a55a;
}

.chat-area__greeting {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 6px;
}

.chat-area--dark .chat-area__greeting {
  color: #f0f0f0;
}

.chat-area__subtitle {
  font-size: 1rem;
  color: #666;
  margin: 0 0 20px;
  line-height: 1.5;
}

.chat-area--dark .chat-area__subtitle {
  color: #aaa;
}

.chat-area__helper {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #999;
  display: block;
  margin-bottom: 10px;
}

.chat-area__topics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 20px;
}

.chat-area__topic-pill {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 20px;
  background: #f5f6f8;
  color: #454545;
  border: 1px solid #e8eaed;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
}

.chat-area__topic-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.chat-area--dark .chat-area__topic-pill {
  background: #3a3f44;
  color: #ddd;
  border-color: #555;
}

.chat-area__suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.chat-area__suggestion-chip {
  font-size: 0.8rem;
  font-weight: 500;
  padding: 8px 14px;
  border-radius: 20px;
  background: #fef4eb;
  color: #f28a3e;
  border: 1px solid rgba(242, 138, 62, 0.25);
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.15s,
    box-shadow 0.15s;
}

.chat-area__suggestion-chip:hover {
  background: #fde8d4;
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(242, 138, 62, 0.15);
}

.chat-area--dark .chat-area__suggestion-chip {
  background: #3d2e1f;
  color: #f5a55a;
  border-color: rgba(242, 138, 62, 0.35);
}

.chat-area--dark .chat-area__suggestion-chip:hover {
  background: #4a3525;
}

.chat-area__error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #fff3f3;
  color: #c0392b;
  font-size: 0.875rem;
  border-top: 1px solid #f5c6cb;
  flex-shrink: 0;
}

.chat-area__error--dark {
  background: #4a2020;
  color: #f5a5a5;
  border-top-color: #6b3030;
}

.chat-area__retry-btn {
  margin-left: auto;
  background: #f28a3e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  flex-shrink: 0;
}

.chat-area__retry-btn:hover {
  background: #e07a2e;
}

@media (max-width: 767px) {
  .chat-area__toggle-btn {
    display: inline-flex;
  }

  .chat-area__header {
    padding: 10px 14px;
  }

  .chat-area__avatar {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }

  .chat-area__welcome {
    padding: 12px 16px 24px;
  }

  .chat-area__greeting {
    font-size: 1.25rem;
  }
}
</style>
